import {
  useCallback,
  useEffect,
} from 'react';
import {
  ProviderEntryConfigDTO,
  VisitNoteDTO,
} from 'dtos';
import {
  useNotify,
} from 'core/hooks';
import {
  useGetAppointmentById,
} from 'pages/Dashboard/pages/Appointments/pages/List/hooks';
import {
  getProviderEntryConfigTarget,
  getProviderEntryConfigLookup,
} from 'pages/Dashboard/components/ReferringProvider/providerEntryConfigTarget';
import {
  useGetProviderEntryConfig,
} from 'pages/Dashboard/components/ReferringProvider/hooks';
import {
  postApiEFaxAddFaxContact,
} from 'endpoints';
import {
  faxEventChannel,
  referringProviderEventChannel,
} from 'pages/Dashboard/services/eventChannels';
import {
  getDisplayName,
  validatePhone,
} from 'pages/Dashboard/utils/helper';

type Params = {
  encounterId: number;
  isFinalized: boolean;
  finalizedVisitNoteMultiMediaId?: VisitNoteDTO['finalizedVisitNoteMultiMediaId'];
  isNoteActionDisabled?: boolean;
};

type SendToReferringProviderDisabledReasonParams = {
  isFinalized: boolean;
  isAppointmentLoading: boolean;
  hasReferringProvider: boolean;
  finalizedVisitNoteMultiMediaId: number;
};

function getSendToReferringProviderDisabledReason({
  isFinalized,
  isAppointmentLoading,
  hasReferringProvider,
  finalizedVisitNoteMultiMediaId,
}: SendToReferringProviderDisabledReasonParams): string | undefined {
  if (!isFinalized) {
    return 'Visit note is not finalized';
  }
  if (!isAppointmentLoading && !hasReferringProvider) {
    return 'Appointment has no referring provider';
  }
  if (!Number.isSafeInteger(finalizedVisitNoteMultiMediaId) || finalizedVisitNoteMultiMediaId <= 0) {
    return 'Finalized visit note PDF is not available';
  }
  return undefined;
}

export default function useSendToReferringProvider({
  encounterId,
  isFinalized,
  finalizedVisitNoteMultiMediaId,
  isNoteActionDisabled,
}: Params) {
  const notify = useNotify();
  const { data: appointment, isLoading, isError } = useGetAppointmentById(
    encounterId, false, false, encounterId > 0,
  );
  const provider = appointment?.referringProvider?.providerEntry;
  const target = provider ? getProviderEntryConfigTarget(provider) : null;
  const mediaId = finalizedVisitNoteMultiMediaId ?? 0;
  const { refetch: refetchProviderConfig, isFetching: isLoadingContact } = useGetProviderEntryConfig(target, false);

  useEffect(() => {
    if (isError) {
      notify({ variant: 'error', message: 'Could not load the appointment details. Please try again.' });
    }
  }, [encounterId, isError, notify]);

  const sendToReferringProviderDisabledReason = isError ? undefined : getSendToReferringProviderDisabledReason({
    isFinalized,
    isAppointmentLoading: isLoading,
    hasReferringProvider: Boolean(provider),
    finalizedVisitNoteMultiMediaId: mediaId,
  });
  const isSendToReferringProviderDisabled = Boolean(sendToReferringProviderDisabledReason || isNoteActionDisabled || isLoading || isError || isLoadingContact);

  const handleSendToReferringProvider = useCallback(async () => {
    if (isSendToReferringProviderDisabled || !provider || !target) {
      return;
    }
    const lookup = getProviderEntryConfigLookup(target);
    if (lookup.kind === 'unavailable') {
      notify({ variant: 'error', message: 'The provider contact is unavailable.' });
      return;
    }
    const openFax = (config: ProviderEntryConfigDTO) => {
      const contact = {
        name: provider.type === 'NPI-2 Organization'
          ? provider.organizationName
          : getDisplayName(provider.formattedNames, `${provider.firstName ?? ''} ${provider.lastName ?? ''}`.trim()),
        faxNumber: config.faxNumber,
      };
      postApiEFaxAddFaxContact(contact).catch(() => {
        console.warn('Could not save the referring provider fax contact.');
      });
      faxEventChannel.emit('onOpenSendFax', {
        patient: appointment?.patient,
        contact,
        patientMultimediaIds: [mediaId],
        attachFaceSheet: false,
      });
    };
    const { data: providerConfig, isError: isContactError } = await refetchProviderConfig();
    if (isContactError) {
      notify({ variant: 'error', message: 'Could not load the provider contact. Please try again.' });
      return;
    }
    if (providerConfig && validatePhone(providerConfig.faxNumber)) {
      openFax(providerConfig);
    } else {
      referringProviderEventChannel.emit('onOpenContactDialog', {
        provider,
        providerConfig: providerConfig ?? null,
        onSaveSuccess: openFax,
      });
    }
  }, [appointment?.patient, isSendToReferringProviderDisabled, mediaId, notify, provider, refetchProviderConfig, target]);

  return {
    sendToReferringProviderDisabledReason,
    isSendToReferringProviderDisabled,
    isLoadingContact,
    handleSendToReferringProvider,
  };
}
